<script lang="ts">
import type { NullableError } from '@/types'
import { ValidationException } from '@/validation-exception'
import { CheckboxInput } from '@goodlifeplus/component-library'
import { ThemeType } from '@goodlifeplus/themes'
import { type PropType, defineComponent } from 'vue'

/**
 * A checkbox input component.
 *
 * @see https://www.w3.org/WAI/tutorials/forms/
 * @see https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-identified.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/G89.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/SCR32
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/ARIA19.html
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA18 On severe errors displays with a alertdialog
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA21
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/alert/examples/alert/
 */
export default defineComponent({
  components: { CheckboxInput },
  inheritAttrs: false,
  props: {
    /**
     * Text describing the reason for the input.
     */
    label: {
      type: String,
    },
    /**
     * The name of the input sent to the API endpoint.
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Text displayed below the input element.
     */
    hint: {
      type: String,
    },
    /**
     * The color scheme of the input element.
     */
    color: {
      type: String,
      default: 'primary',
    },
    /**
     * @model
     */
    modelValue: {
      type: [Number, String, Boolean],
      required: true,
    },
    /**
     * Displays input validation errors returned.
     */
    errors: {
      type: Object as PropType<NullableError>,
    },
    /**
     * Adds classes to the container of an input field.
     */
    containerClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value: number | string) {
        this.$emit('update:modelValue', value)
      },
    },
    required(): boolean {
      return (this.$attrs.required ?? false) as boolean
    },
    disabled(): boolean {
      return (this.$attrs.disabled ?? false) as boolean
    },
    hasErrors(): boolean {
      return this.inputErrors.length > 0
    },
    inputErrors() {
      return this.errors instanceof ValidationException
        ? this.errors.get(this.name)
        : []
    },
    style(): string {
      if (this.$theme.type === ThemeType.Club) {
        return 'border-secondary checked:bg-secondary hover:checked:bg-secondary focus:checked:bg-secondary checked:text-pure-white'
      }

      return this.color === 'primary'
        ? 'border-primary checked:bg-primary hover:checked:bg-primary focus:checked:bg-primary'
        : 'border-[#8f8f8f] checked:bg-secondary hover:checked:bg-secondary focus:checked:bg-secondary text-black'
    },
  },
})
</script>

<template>
  <div
    :class="[disabled ? 'opacity-50' : '', containerClass]"
    class="relative mb-3 flex flex-grow flex-col gap-2"
  >
    <label :for="name" class="flex items-center hover:cursor-pointer gap-3">
      <CheckboxInput
        :id="name"
        v-bind="$attrs"
        v-model="value"
        :aria-errormessage="name + '-errors'"
        :aria-describedby="name + '-hint'"
        :aria-invalid="hasErrors"
        :autocomplete="name"
        :name
      />
      <!-- <span v-if="required" class="ml-2 text-xs">*</span> -->
      <span v-html="label" />
    </label>

    <div
      v-if="hasErrors"
      :id="name + '-errors'"
      :aria-live="hasErrors ? 'assertive' : 'off'"
      aria-atomic="true"
      class="errors"
    >
      <div
        v-for="error in inputErrors"
        :key="error"
        class="text-xs font-bold text-red-500"
      >
        {{ error }}
      </div>
    </div>

    <div v-if="hint && !hasErrors" :id="name + '-hint'" class="text-xs">
      {{ hint }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
input[type='checkbox']:checked {
  &.checked:text-pure-white {
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  }

  &:not(.checked:text-pure-white) {
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='currentColor' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  }
}
</style>
